/**
 * @typedef {import('../MapLibreMap.js').MLD} MLD
 */

/**
 * @typedef {"cluster" | "primary_marker" | "user" | "custom"} MarkerType
 */

/**
 * @typedef {Object} IBasicMarkerProps
 * @property {MLD._LngLatLike=} lngLat
 * @property {MLD.MarkerOptions=} markerOptions
 * @property {string|null=} markerID
 * @property {MLD._LngLatBounds=} bounds
 * @property {boolean=} outOfBounds
 * @property {boolean=} preventPropsPositionUpdate
 * @property {OSM.MapMarkerIcon=} icon
 * @property {IMapMarkerIconProps=} iconProps
 * @property {IMapPopupProps=} popupProps
 * @property {boolean=} centerOnClick
 * @property {MLD._PointLike=} focusOffset
 * @property {string[]=} objectIDs
 * @property {HTMLElement|string|((marker:OSM.MapMarker) => HTMLElement|string)|null=} popupContent
 * @property {string|number=} defaultZIndex
 * @property {number|null=} defaultZoomLevel
 * @property {boolean=} fallbackToBoundsZoomLevel
 * @property {((e:MLD.MapLibreEvent<MouseEvent | TouchEvent | undefined>) => string)|null=} onDragEnd
 * @property {((e:MLD._MapMouseEvent) => string)|null=} onClick
 * @property {string=} cursor
 */


/**
 * @typedef {Object} IMarkerAdditionalProps
 * @property {MLD._LngLatLike} lngLat
 * @typedef {IBasicMarkerProps & IMarkerAdditionalProps} IMarkerProps
 */

/**
 * @typedef {Object.<string, IMarkerProps>} IMarkersProps
 */


window.OSM = window.OSM ?? {};
OSM.MapMarker = class MapMarker {

    /** @type {OSM.MapLibreMap} */
    map;
    /** @type {MarkerType|null} */
    markerType;
    /** @type {IMarkerProps} */
    props;



    /** @type {IMapPopupProps|null} */
    popupProps = null;
    /** @type {MLD._Point|null} */
    focusOffset = null;
    /** @type {HTMLElement|string|((marker:OSM.MapMarker) => HTMLElement|string)|null} */
    popupContent = null;

    /** @type {OSM.MapMarkerIcon} */
    icon = null;
    /** @type {MLD.MarkerOptions|null} */
    markerOptions = null;
    /** @type {MLD.Marker} */
    marker;
    /** @type {string|null} */
    selectedObjectID = null;
    selectedMarkerZIndex = 6;
    /** @type {string|null} */
    markerID = null;
    centerOnClick = false;
    /** @type {number|null} */
    defaultZoomLevel = null;

    addedToMap = false;
    clickable = false;

    /** @type {string|null} */
    cursor = null;

    /**
     *
     * @param {OSM.MapLibreMap} map
     * @param {MarkerType|null} markerType
     * @param {IMarkerProps} props
     * @param {boolean=} isExtended
     */
    constructor(map, markerType, props, isExtended) {
        this.map = map;
        this.markerType = markerType;
        this.props = props ?? {};
        if (!isExtended) {
            this.initialize();
        }
    }

    initialize(){
        this.bindFunctions();
        this.applyProps();
        this.constructMarker();
    }

    bindFunctions()
    {
        this.handleDragEnd = this.handleDragEnd.bind(this);
        this.handleOnClick = this.handleOnClick.bind(this);
    }

    applyProps(){

        const props = this.props;

        this.popupProps = props.popupProps ?? null;
        this.popupContent = props.popupContent ?? null;

        this.markerID = props.markerID ?? null;

        this.focusOffset = props.focusOffset ? OSM.requirePoint(props.focusOffset) : null;
        this.icon = props.icon ?? (!props.iconProps.imagePath ? new OSM.MapMarkerIcon(props.iconProps) : new OSM.MapMarkerImageIcon(props.iconProps));

        const markerElement = this.applyAnchorTranslationToIcon(this.icon.element)

        this.markerOptions = {
            ...this.props.markerOptions,
            element: markerElement
        }

        this.centerOnClick = props.centerOnClick ?? false;
        this.defaultZoomLevel = props.defaultZoomLevel ?? null;
        this.clickable = this.centerOnClick || !!this.props.popupContent || this.props.onClick;
        this.cursor = this.props.cursor ?? (this.clickable ? "pointer" : null);
    }

    /**
     *
     * @param iconElement {HTMLElement}
     * @returns {HTMLElement}
     */
    applyAnchorTranslationToIcon(iconElement)
    {
        const markerElement = document.createElement("div");
        const translateElement = document.createElement("div");
        markerElement.remove();
        translateElement.remove();
        markerElement.appendChild(translateElement);
        translateElement.appendChild(iconElement);

        const anchor = this.props.markerOptions?.anchor ?? 'center';
        let adjustLeft = 0
        let adjustTop = 0
        if((anchor === "center") || (anchor === "top") || (anchor === "bottom")){
            adjustLeft = -50;
        } else if((anchor === "top-right") || (anchor === "right") || (anchor === "bottom-right")){
            adjustLeft = -100;
        }
        if((anchor === "left") || (anchor === "center") || (anchor === "right")){
            adjustTop = -50;
        } else if((anchor === "bottom-left") || (anchor === "bottom") || (anchor === "bottom-right")){
            adjustTop = -100;
        }
        if(adjustLeft || adjustTop){
            translateElement.style.transform = 'translate(' + adjustLeft +'%, ' + adjustTop + '%)';
        }

        return markerElement
    }

    applyCurrentZIndex()
    {
        this.marker.getElement().style.zIndex = this.map.selectedMarker !== this ? this.props.defaultZIndex ?? "" : this.selectedMarkerZIndex;
    }

    /**
     *
     * @returns {MLD._LngLatBounds|null}
     */
    appliedBounds(){
        return this.props.bounds ?? null;
    }

    /**
     *
     * @param {MLD.MapLibreEvent<MouseEvent | TouchEvent | undefined>} event
     */
    handleDragEnd = (event) => {
        if(this.marker.isDraggable()){
            this.map.onMarkerPositionChange(this);
        }
        if(this.props.onDragEnd){
            this.props.onDragEnd(event);
        }
    }


    /**
     *
     * @param {MouseEvent & {clickOnLayer:boolean=}} event
     */
    handleOnClick = (event) => {
        event.clickOnLayer = true
        const map = this.map;
        if(this.props.popupContent){
            if(map.selectedMarker !== this || !map.showSelectedMarkerPopup){
                map.selectMarker(this, this.props.centerOnClick, true)
            } else {
                this.map.popup?.closePopup();
            }
        } else if(this.centerOnClick){
            map.selectMarker(this, true)
        }
        if (this.props.onClick)
        {
            this.props.onClick(event);
        }
    }

    /**
     *
     * @param {MLD._LngLatLike} lngLat
     * @returns {OSM.MapMarker}
     */
    setLngLat(lngLat){
        this.marker.setLngLat(lngLat);
        this.map.onMarkerPositionChange(this);
        return this;
    }

    /**
     *
     * @returns {MLD._LngLat}
     */
    getLngLat()
    {
        return this.marker.getLngLat()
    }

    /**
     *
     * @returns {boolean}
     */
    isAtDefaultPosition()
    {
        const lngLat = this.getLngLat();
        const propsLngLat = this.props.lngLat ? maplibregl.LngLat.convert(this.props.lngLat) : null;
        return (lngLat.lat === propsLngLat.lat) && (lngLat.lng === propsLngLat.lng)
    }

    /**
     *
     * @returns {string|null}
     */
    getSelectedObjectID()
    {
        if(this.selectedObjectID && this.props.objectIDs?.includes(this.selectedObjectID)){
            return this.selectedObjectID;
        }
        if((this.props.objectIDs ?? []).length){
            return this.props.objectIDs[0];
        }
        return null;
    }

    onPopupClose()
    {
        this.selectedObjectID = null;
    }

    addToMap()
    {
        if(!this.map.map || this.addedToMap){
            return;
        }
        this.addedToMap = true;
        this.marker.addTo(this.map.map);
        if(this.clickable){
            this.marker.getElement().addEventListener('click', this.handleOnClick)
        }
        if(this.markerOptions.draggable){
            this.marker.on('dragend', this.handleDragEnd);
        }
        this.applyCurrentZIndex()
    }

    removeFromMap()
    {
        if(!this.addedToMap){
            return;
        }
        if(this.map.selectedMarker === this){
            this.map.deselectSelectedMarker();
        }
        this.addedToMap = false;
        this.marker.getElement().removeEventListener('click', this.handleOnClick)
        this.marker.off('dragend', this.handleDragEnd);
        this.marker.remove();
    }

    constructMarker(){
        const marker = new maplibregl.Marker({
            ...this.markerOptions,
            anchor: "top-left"
        });
        this.marker = marker;
        if(this.cursor){
            marker.getElement().style.cursor = this.cursor;
        }
        if(this.props.lngLat){
            this.setLngLat(this.props.lngLat);
        }
        const enableContextMenu = this.map.props.enableContextMenu ?? (this.map.coreConfig.enableContextMenu ?? false);
        if(enableContextMenu){

            this.marker?._element?.addEventListener("contextmenu", (event) => {
                event.stopImmediatePropagation();
            })
        }
    }
}